import React, { useMemo } from "react";
import PropTypes from "prop-types";
import ConstrainedSelect from "./ConstrainedSelect";
import { collectSelectOptions } from "../utils/collectSelectOptions";

/**
 * @typedef {{
 *   target: { value: string, name: string, id: string },
 *   currentTarget: { value: string, name: string, id: string },
 * }} ViewportSelectChangeEvent
 */

/**
 * Global replacement for native <select>. Chrome's native popup ignores CSS and can leave the viewport.
 *
 * @param {{
 *   id?: string,
 *   className?: string,
 *   value?: string | number,
 *   onChange?: (event: ViewportSelectChangeEvent) => void,
 *   disabled?: boolean,
 *   name?: string,
 *   title?: string,
 *   required?: boolean,
 *   "aria-label"?: string,
 *   "aria-busy"?: boolean,
 *   style?: import("react").CSSProperties,
 *   children?: import("react").ReactNode,
 * }} props
 */
export default function ViewportSelect({
  id,
  className = "",
  value = "",
  onChange,
  disabled = false,
  name = "",
  title,
  required = false,
  "aria-label": ariaLabel,
  "aria-busy": ariaBusy = false,
  style,
  children,
}) {
  const options = useMemo(() => collectSelectOptions(children), [children]);

  return (
    <ConstrainedSelect
      id={id}
      className={className}
      value={value == null ? "" : String(value)}
      disabled={disabled}
      ariaLabel={ariaLabel}
      style={style}
      title={title}
      required={required}
      ariaBusy={ariaBusy}
      onChange={(next) => {
        if (typeof onChange !== "function") {
          return;
        }
        const eventTarget = { value: next, name, id: id || "" };
        onChange({ target: eventTarget, currentTarget: eventTarget });
      }}
      options={options}
    />
  );
}

ViewportSelect.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  title: PropTypes.string,
  required: PropTypes.bool,
  "aria-label": PropTypes.string,
  "aria-busy": PropTypes.bool,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  children: PropTypes.node,
};
